import React, { useMemo } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';


const DataTable = ({ columns, data }) => {
    // Crear columnas y datos para `react-table`
    const memoizedColumns = useMemo(() => columns.map(col => ({
        Header: col, // Nombre de la columna
        accessor: col, // Acceso a los datos
    })), [columns]);

    const memoizedData = useMemo(() => data, [data]);

    // Usar `useTable`, `useSortBy` para ordenar y `usePagination` para paginación
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page, // En lugar de `rows`, usamos `page` para obtener la página actual
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns: memoizedColumns,
            data: memoizedData,
            initialState: { pageIndex: 0, pageSize: 20 }, // Por defecto 20 por página
        },
        useSortBy, // Agregar ordenamiento
        usePagination // Agregar paginación
    );

    // Manejar el cambio de página en el input al presionar enter
    const handlePageInput = (e) => {
        if (e.key === 'Enter') {
            const page = e.target.value ? Number(e.target.value) - 1 : 0;
            if (page >= 0 && page < pageOptions.length) {
                gotoPage(page);
            }
        }
    };

    return (
        <div className="table-container">
            <table className="data-table" {...getTableProps()}>
                <thead>
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render('Header')}
                                    {/* Iconos para ordenar */}
                                    <span>
                                        {column.isSorted
                                            ? column.isSortedDesc
                                                ? ' 🔽' // Icono para descendente
                                                : ' 🔼' // Icono para ascendente
                                            : ' ⬍'} {/* Mostrar ícono ⬍ si no está ordenado */}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map(cell => (
                                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Paginación */}
            <div className="pagination">
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                    {'<<'}
                </button>
                <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                    {'<'}
                </button>
                <button onClick={() => nextPage()} disabled={!canNextPage}>
                    {'>'}
                </button>
                <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>
                    {'>>'}
                </button>
                <span>
                    Página{' '}
                    <strong>
                        {pageIndex + 1} de {pageOptions.length}
                    </strong>{' '}
                </span>
                <span>
                    | Ir a la página:{' '}
                    <input
                        type="number"
                        defaultValue={pageIndex + 1}
                        onKeyDown={handlePageInput} // Llama a handlePageInput al presionar enter
                        style={{ width: '100px' }}
                    />
                </span>
                <select
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value)); // Actualizar el número de registros a mostrar
                        gotoPage(0); // Resetear a la página 0 cuando se cambia el tamaño de página
                    }}
                >
                    {[10, 20, 30, 40, 50].map(pageSize => (
                        <option key={pageSize} value={pageSize}>
                            Mostrar {pageSize}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default DataTable;
